import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Canonical from './Canonical';

function TrustAndSafety() {
  const [activeTab, setActiveTab] = useState('ethics');

  const EthicsPolicy = () => (
    <div className="space-y-8">
      <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-r-xl">
        <h2 className="text-xl font-bold text-blue-900 mb-2">Our AI Ethics Manifesto</h2>
        <p className="text-blue-800 text-sm">
          At Remote Assistant AI, we believe automation should empower humans, not deceive them. Our ethics policy is built on the "Human-in-the-Loop" principle.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[
          {
            title: "Transparency First",
            desc: "Our AI will always identify itself as an automated assistant. We strictly prohibit 'Ghosting' or impersonating a human being."
          },
          {
            title: "Data Sovereignty",
            desc: "Your data is yours. We do not use your proprietary business conversations to train global models for other customers."
          },
          {
            title: "Bias Mitigation",
            desc: "We actively audit our LLM implementations to ensure fair treatment across all accents, dialects, and demographics."
          },
          {
            title: "The Human Override",
            desc: "Every AI interaction includes a clear 'escape hatch' to a human operator, ensuring complex needs are never ignored."
          }
        ].map((item, idx) => (
          <div key={idx} className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
            <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
            <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );

  const DPA = () => (
    <div className="bg-white border rounded-xl p-8 shadow-sm">
      <div className="space-y-6 text-sm text-gray-700">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 border-b pb-4 mb-4">Data Processing Addendum (DPA)</h2>
          <p className="italic text-gray-600">Last Updated: March 2024</p>
        </div>
        
        <section>
          <h3 className="font-bold text-gray-900 mb-2">1. Nature of the Relationship</h3>
          <p className="leading-relaxed">
            This DPA applies to the processing of Personal Data by Remote Assistant AI (the "Processor") on behalf of the Client (the "Controller"). This agreement ensures compliance with GDPR, CCPA, and the EU AI Act.
          </p>
        </section>

        <section className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <h3 className="font-bold text-gray-900 mb-2">2. AI-Specific Data Usage (Crucial)</h3>
          <p className="font-medium text-blue-700 mb-2 underline">The "No-Global-Training" Guarantee:</p>
          <p className="leading-relaxed">
            Processor warrants that Client's Personal Data and conversational inputs shall NOT be utilized to train, retrain, or improve foundational large language models (LLMs) used across the Processor's broader customer base. Data is siloed to the Client's specific instance.
          </p>
        </section>

        <section>
          <h3 className="font-bold text-gray-900 mb-2">3. Technical & Organizational Measures</h3>
          <ul className="list-disc pl-5 space-y-2 leading-relaxed">
            <li><strong>Encryption:</strong> All conversational data is encrypted at rest (AES-256) and in transit (TLS 1.2+).</li>
            <li><strong>Access Control:</strong> Access to customer logs is restricted to authorized personnel via Multi-Factor Authentication (MFA).</li>
            <li><strong>Data Minimization:</strong> Processor automatically redacts or deletes PII after a period of 30 days unless otherwise configured by the Controller.</li>
          </ul>
        </section>

        <section>
          <h3 className="font-bold text-gray-900 mb-2">4. Sub-processors</h3>
          <p className="leading-relaxed mb-3">
            Client acknowledges and agrees that Processor utilizes the following sub-processors for AI computation:
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  <th className="p-2 border border-gray-300 text-left font-semibold">Entity</th>
                  <th className="p-2 border border-gray-300 text-left font-semibold">Role</th>
                  <th className="p-2 border border-gray-300 text-left font-semibold">Data Location</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="p-2 border border-gray-300 font-medium">OpenAI</td>
                  <td className="p-2 border border-gray-300">LLM Processing</td>
                  <td className="p-2 border border-gray-300">US</td>
                </tr>
                <tr>
                  <td className="p-2 border border-gray-300 font-medium">AWS Bedrock</td>
                  <td className="p-2 border border-gray-300">LLM Processing</td>
                  <td className="p-2 border border-gray-300">US</td>
                </tr>
                <tr>
                  <td className="p-2 border border-gray-300 font-medium">Groq Cloud</td>
                  <td className="p-2 border border-gray-300">LLM Processing</td>
                  <td className="p-2 border border-gray-300">US</td>
                </tr>

                <tr>
                  <td className="p-2 border border-gray-300 font-medium">Stripe</td>
                  <td className="p-2 border border-gray-300">Payment Processing</td>
                  <td className="p-2 border border-gray-300">Global</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );

  return (
    <>
      <Canonical path="/trust-and-safety" />
      <nav className="flex justify-between items-center px-6 py-4 bg-white shadow-sm fixed top-0 w-full z-50">
        <div className="text-xl font-bold text-primary">
          <Link to="/" className="text-primary no-underline">Remote Assistant</Link>
        </div>
        <div className="flex items-center gap-6">
          <Link to="/" className="text-gray-700 hover:text-primary no-underline">Home</Link>
          <Link to="/signup" className="px-6 py-3 rounded-xl bg-primary hover:bg-primary-hover text-white font-semibold shadow no-underline">
            Get Started
          </Link>
        </div>
      </nav>

      <section className="pt-24 pb-16 px-8 max-w-4xl mx-auto">
        <div className="bg-white p-12 rounded-2xl shadow-sm border">
          <h1 className="text-4xl font-bold text-gray-900 mb-4 text-center">Trust & Safety</h1>
          <p className="text-center text-gray-600 mb-8">
            Built on transparency. Our commitment to ethics, privacy, and the responsible use of AI.
          </p>

          {/* Navigation Tabs */}
          <div className="mb-8">
            <div className="bg-gray-100 rounded-xl p-2 flex gap-2">
              <button 
                onClick={() => setActiveTab('ethics')}
                className={`flex-1 py-3 px-4 rounded-lg font-bold transition-all ${
                  activeTab === 'ethics' 
                    ? 'bg-blue-600 text-white shadow-lg' 
                    : 'text-gray-600 hover:bg-gray-200'
                }`}
              >
                Ethics Policy
              </button>
              <button 
                onClick={() => setActiveTab('dpa')}
                className={`flex-1 py-3 px-4 rounded-lg font-bold transition-all ${
                  activeTab === 'dpa' 
                    ? 'bg-blue-600 text-white shadow-lg' 
                    : 'text-gray-600 hover:bg-gray-200'
                }`}
              >
                Data Addendum (DPA)
              </button>
            </div>
          </div>

          {/* Content Area */}
          <div className="mb-8">
            {activeTab === 'ethics' ? <EthicsPolicy /> : <DPA />}
          </div>

          {/* Footer Note */}
          <div className="mt-12 p-8 bg-gray-50 border border-dashed border-gray-300 rounded-2xl text-center">
            <h3 className="font-bold text-gray-800 mb-2">Need a custom agreement?</h3>
            <p className="text-gray-600 text-sm mb-6">
              Enterprise clients with specific compliance needs can request a custom SLA and DPA.
            </p>
            <Link to="/contact" className="text-blue-600 font-bold hover:text-blue-700">
              Contact our Legal Team →
            </Link>
          </div>
        </div>
      </section>

      <footer className="py-8 text-center text-gray-500 text-xs border-t">
        © 2024 Remote Assistant AI. Compliant with EU AI Act, GDPR, and CCPA standards.
      </footer>
    </>
  );
}

export default TrustAndSafety;
